import React, {MutableRefObject, useImperativeHandle, useState} from "react";
import {arrayUpdatePartial, ArrayUpdateType} from "../../immutableState";
import CheckBox from "../../components/CheckBox";
import {classNames} from "../../wrapper";
import Input from "../../components/Input";
import TestType from "../../controllers/TestType";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import {useValidationArray} from "../validationArray";
import {FarmerRow} from "./SubmissionPrepareFarmer";
import GenericSelect from "../../components/GenericSelect";

export interface SubmissionPrepareFunctions {
    validate: () => boolean;
}

const SubmissionTable: React.FC<{
    leafs: (FarmerRow)[]
    data: SubmissionDataResponse<FarmerRow>
    setData: (data: (FarmerRow)[]) => void
    index: number
    submitRef?: MutableRefObject<SubmissionPrepareFunctions | undefined>;
}> = (props) => {
    
    const [headerTest, setHeaderTest] = useState<TestType | null>(null)
    const [headerOptionalTests, setHeaderOptionalTests] = useState<TestType[]>([]);
    const [split , setSplit] = useState<boolean>(false)

    useImperativeHandle(props.submitRef, () => ({
        validate: () => validation.validate()
    }));
    
    function lengthCheck(length: number, deleted: boolean) {
        // if it's deleted, it's fine and the validation should pass
        return length > 0 || deleted;
    }

    function duplicatesampleNumberCheck(sampleNumber: string) {
        return props.leafs.filter(l => l.sampleNumber === sampleNumber).length === 1;
    }

    const validation = useValidationArray(props.leafs)({
        sampleNumber: leaf => lengthCheck(leaf.sampleNumber.length, leaf.delete) && duplicatesampleNumberCheck(leaf.sampleNumber),
        block: leaf => lengthCheck(leaf.blockNumber.length, leaf.delete),
        test: leaf => leaf.test != TestType.Unknown 
            && leaf.test != TestType.ItestFarmerSample,  
    })
    
    function setDataIndex(index: number, update: ArrayUpdateType<FarmerRow>) {
        props.setData(arrayUpdatePartial( props.leafs, index, update))
    }
    
    function setLeafData(update: (leaf: FarmerRow) => FarmerRow) {
        const leafs = props.leafs;
        props.setData(leafs.map((l, i) => {
            return update(l)
        }))
    }
    function updateHeaderTest(test: TestType) {
        setHeaderTest(test);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, test: test};
        })
    }

    function updateHeaderOptionalTest(optionalTests: TestType[]) {
        setHeaderOptionalTests(optionalTests);

        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, optionalTests: [...optionalTests]};
        })
    }

    function setDataIndexHeader(index: number, update: ArrayUpdateType<FarmerRow>) {
        props.setData(arrayUpdatePartial( props.leafs, index, update))
    }
    
    function changeCheckbox(index: number, v: boolean) {
        props.setData(props.leafs.map((l, i) => i===index ? {...l, selected: v} : l))
        if (!props.leafs.some(l => l.selected)) setSplit(false)
        setDataIndex(index, {selected: v})
    }
    
    function updateSelectAll(v: boolean) {
        setLeafData(l => ({...l, selected: v}))
        props.setData(props.leafs.map(l => ({...l, selected: v})))
    }

    return <div> 
        <table className="w-full">
        <thead>
        <tr className='bg-gray-100 '>
            <th className="p-2 text-xs align-bottom">
                <div className='text-center'>
                    <div>Select</div>
                    <div className="text-center">
                        <CheckBox checked={props.leafs.every(l => l.selected)}
                                  onChange={v => updateSelectAll(v)}/>
                    </div>
                </div>
            </th>
            <th className="p-2 text-xs align-bottom">Sample Number</th>
            <th className="p-2 text-xs align-bottom">Block</th>
            <th className="p-2 text-xs align-bottom">
                <div>Tests</div>
                <GenericSelect
                    options={props.data.tests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={headerTest}
                    onChange={v => updateHeaderTest(v as TestType)}/>
            </th>
            <th className="p-2 text-xs align-bottom">
                <div>Optional Tests</div>
                <GenericSelect
                    options={props.data.optionalTests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={headerOptionalTests || []}
                    multiple={true}
                    onChange={v => {
                        updateHeaderOptionalTest(v as TestType[])
                    }}
                />
            </th>
            <th className="text-xs align-bottom">Actions</th>
        </tr>
        </thead>
            <tbody>
            {validation.items.map((leaf, index) =>
                <tr className={classNames(index % 2 === 0 ? '' : 'bg-gray-100', 'group hover:bg-gray-200 cursor-pointer')}
                key={index}>
                <td className={classNames("table-data text-center", leaf.data.delete ? 'inactive' : '')}>
                    <div className='flex-col items-end'>
                        <CheckBox checked={leaf.data.selected || false} onChange={v => changeCheckbox(index, v)}/>
                    </div>
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <Input 
                        className={leaf.rules.sampleNumber ? '' : 'border-1 border-red-600'} 
                        value={leaf.data.sampleNumber} 
                        change={v => setDataIndex(index, {sampleNumber: v})}/>
                    {leaf.data.notes}
                </td>
                <td className={classNames("table-data", leaf.data.delete ? 'inactive' : '')}>
                    <Input
                        className={leaf.rules.block ? '' : 'border-1 border-red-600'}
                        value={leaf.data.blockNumber} 
                        change={v => setDataIndex(index, {blockNumber: v})}/>
                </td>
                <td> 
                    <GenericSelect
                        className={leaf.rules.test  ? '' : 'border-1 border-red-600'}
                        options={props.data.tests} 
                        textFunc={c => c.value} 
                        valueFunc={c => c.key}
                        value={leaf.data.test} 
                        onChange={v => setDataIndex(index, {test: v as TestType})}/>
                </td>
                <td>
                    <GenericSelect
                        options={props.data.optionalTests} 
                        textFunc={c => c.value} 
                        valueFunc={c => c.key}
                        value={leaf.data.optionalTests || []}
                        multiple={true}
                        onChange={v => {
                            setDataIndex(index, {optionalTests: v as TestType[]})
                        }}
                        />
                </td>
                <td className="table-data">
                    {leaf.data.delete ? <div className="btn-sm btn-primary"
                                        onClick={() => setDataIndex(index, {delete: false})}>+</div> : null}
                    {!leaf.data.delete ? <div className="btn-sm btn-error"
                                         onClick={() => setDataIndex(index, {delete: true})}>-</div> : null}
                </td>
            </tr>)}
        </tbody>
    </table>
    </div>
}

export default SubmissionTable;